import React, { useState, useEffect, createContext, useContext } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { identifyUser, clearUserIdentification } from '../utils/analytics';
import { invalidateTeamMembersCache } from '../utils/teamMembersCache';

// Organization membership info returned with user
interface OrganizationMembership {
  organization_id: string;
  organization_name: string;
  role: 'ORG_ADMIN' | 'ORG_VIEWER';
  practice_count: number;
}

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  full_name: string;
  practice_id?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  balance_credits?: number;  // Credits in dollars
  photo_url?: string;
  // Organization fields (populated from backend)
  organization_memberships?: OrganizationMembership[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  user: User | null;
  login: (username: string, token: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
  // Organization helpers
  isOrgAdmin: boolean;
  hasMultiplePractices: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app startup
    const token = localStorage.getItem('token');
    const storedUsername = localStorage.getItem('username');
    
    if (token && storedUsername) {
      setIsAuthenticated(true);
      setUsername(storedUsername);
      // Fetch user details
      fetchUserDetails();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.AUTH.ME, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Track user in GA4
        identifyUser(userData.id, userData.role, userData.practice_id);
      } else {
        // If token is invalid (401, 403, etc.), clear auth state
        console.error('Failed to fetch user details - invalid token');
        localStorage.removeItem('token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('username');
        setIsAuthenticated(false);
        setUsername(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      // On network error or other failure, clear auth state to be safe
      localStorage.removeItem('token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('username');
      setIsAuthenticated(false);
      setUsername(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, token: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('username', username);
    setIsAuthenticated(true);
    setUsername(username);
    // Fetch user details after login and wait for completion
    await fetchUserDetails();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
    setUsername(null);
    setUser(null);
    
    // Clear user tracking in GA4
    clearUserIdentification();
    
    // Clear team members cache so next login gets fresh data
    invalidateTeamMembersCache();
    
    // Clear vet selection so it re-initializes with all members on next login
    localStorage.removeItem('helppet_vet_selection');
    localStorage.removeItem('helppet_vet_selection_initialized');
  };

  // Computed properties for organization access
  const isOrgAdmin = user?.role === 'ORG_ADMIN' || 
    user?.organization_memberships?.some(m => m.role === 'ORG_ADMIN') || false;
  
  const hasMultiplePractices = (user?.organization_memberships?.some(m => m.practice_count > 1)) || false;

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      username, 
      user, 
      login, 
      logout, 
      refreshUser: fetchUserDetails, 
      isLoading,
      isOrgAdmin,
      hasMultiplePractices
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
